/**
 * Hiển thị datetime lịch học theo giờ Việt Nam (khớp wall-clock trong MySQL DATETIME).
 *
 * Backend mysql2 timezone +07:00 → API trả ISO UTC đúng instant;
 * UI format cố định Asia/Ho_Chi_Minh (không phụ thuộc browser TZ, không strip chữ số Z).
 */

export type DbDateInput = string | Date | number | null | undefined

const VN_TZ = 'Asia/Ho_Chi_Minh'
const WEEKDAY_SHORT_VI = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const

function pad2(n: number | string): string {
  return String(n).padStart(2, '0')
}

/**
 * Parse API/DB value thành absolute Date.
 * Chuỗi naive `YYYY-MM-DD HH:mm:ss` (không Z/offset) = wall-clock VN (+07:00).
 * Chuỗi ISO có Z/offset = instant chuẩn.
 */
export function toVnDate(raw?: DbDateInput): Date | null {
  if (raw == null) return null
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw
  }
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const s = String(raw).trim()
  if (!s) return null

  // MySQL / datetime-local naive: ... không có Z hay ±offset
  const naive = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  )
  if (naive) {
    const [, y, mo, da, h = '00', mi = '00', sec = '00'] = naive
    const d = new Date(`${y}-${mo}-${da}T${h}:${mi}:${sec}+07:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Parts theo Asia/Ho_Chi_Minh (đúng giờ trong DB wall-clock). */
export function getVnParts(raw?: DbDateInput): {
  y: string
  mo: string
  da: string
  h: string
  mi: string
  sec: string
  weekday: number
} | null {
  const d = toVnDate(raw)
  if (!d) return null

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: VN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'short',
  })
  const bag: Record<string, string> = {}
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== 'literal') bag[p.type] = p.value
  }
  // en-US hour12:false can yield "24" for midnight in some engines
  let h = bag.hour ?? '00'
  if (h === '24') h = '00'

  // weekday index from a date constructed in local from VN y/mo/da (calendar day in VN)
  const y = bag.year!
  const mo = bag.month!
  const da = bag.day!
  const localNoon = new Date(Number(y), Number(mo) - 1, Number(da), 12, 0, 0)

  return {
    y,
    mo,
    da,
    h: pad2(h),
    mi: pad2(bag.minute ?? '00'),
    sec: pad2(bag.second ?? '00'),
    weekday: localNoon.getDay(),
  }
}

/** Gửi API: wall-clock VN trong form datetime-local → ISO có offset +07:00 */
export function toDbDateTimePayload(raw?: DbDateInput): string {
  if (raw == null || String(raw).trim() === '') return ''
  // datetime-local: YYYY-MM-DDTHH:mm — luôn coi là giờ VN
  const s = String(raw).trim()
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (m) {
    const [, y, mo, da, h, mi, sec = '00'] = m
    return `${y}-${mo}-${da}T${h}:${mi}:${sec}+07:00`
  }
  const d = toVnDate(raw)
  if (!d) return ''
  return d.toISOString()
}

/** `<input type="datetime-local">` = giờ VN */
export function toDatetimeLocalValue(raw?: DbDateInput): string {
  const p = getVnParts(raw)
  if (!p) return ''
  return `${p.y}-${p.mo}-${p.da}T${p.h}:${p.mi}`
}

export function formatDbDateVi(raw?: DbDateInput): string {
  const p = getVnParts(raw)
  if (!p) return 'Chưa có thông tin'
  return `${p.da}/${p.mo}/${p.y}`
}

export function formatDbTimeVi(raw?: DbDateInput): string {
  const p = getVnParts(raw)
  if (!p) return '-'
  return `${p.h}:${p.mi}`
}

export function formatDbDateTimeVi(raw?: DbDateInput): string {
  const p = getVnParts(raw)
  if (!p) return '-'
  return `${p.da}/${p.mo}/${p.y}, ${p.h}:${p.mi}`
}

/** `T3, 05/08/2026 · 20:00–22:00` theo giờ VN */
export function formatDbDateTimeRangeVi(start?: DbDateInput, end?: DbDateInput): string {
  const s = getVnParts(start)
  const e = getVnParts(end)
  if (!s) return 'Chưa có thông tin'

  const weekday = WEEKDAY_SHORT_VI[s.weekday] ?? ''
  const day = `${weekday}, ${s.da}/${s.mo}/${s.y}`
  const startTime = `${s.h}:${s.mi}`
  const endTime = e ? `${e.h}:${e.mi}` : ''
  return endTime ? `${day} · ${startTime}–${endTime}` : `${day} · ${startTime}`
}

/** @deprecated alias — parse parts theo giờ VN */
export function parseDbDateParts(raw?: DbDateInput) {
  const p = getVnParts(raw)
  if (!p) return null
  return { y: p.y, mo: p.mo, da: p.da, h: p.h, mi: p.mi, sec: p.sec }
}

export function vnYmdKey(raw?: DbDateInput): string | null {
  const p = getVnParts(raw)
  if (!p) return null
  return `${p.y}-${p.mo}-${p.da}`
}

/**
 * Lớp còn mở đăng ký nếu chưa qua ngày khai giảng (cho phép đúng ngày KG).
 * Không có startDate → coi như còn mở.
 */
export function isClassroomOpenForEnrollment(startDate?: DbDateInput, asOf: Date = new Date()): boolean {
  const startKey = vnYmdKey(startDate)
  if (!startKey) return true
  const todayKey = vnYmdKey(asOf)
  if (!todayKey) return true
  return startKey >= todayKey
}

/** Đã qua ngày khai giảng (so sánh theo ngày lịch VN). */
export function isClassroomPastStartDate(startDate?: DbDateInput, asOf: Date = new Date()): boolean {
  if (!startDate) return false
  const startKey = vnYmdKey(startDate)
  const todayKey = vnYmdKey(asOf)
  if (!startKey || !todayKey) return false
  return startKey < todayKey
}

export type ClassroomLifecycleStatus =
  | 'UPCOMING'
  | 'LEARNING'
  | 'FINISH'
  | 'STOP'
  | 'CANCELLED'

/**
 * Trạng thái lớp theo ngày (Asia/Ho_Chi_Minh, so sánh theo ngày lịch):
 * - today > end_date → FINISH (Hoàn thành)
 * - today >= start_date → LEARNING (Đang học)
 * - else → UPCOMING (Sắp khai giảng)
 * STOP / CANCELLED giữ nguyên nếu có trong DB.
 */
export function resolveClassroomLifecycleStatus(
  startDate?: DbDateInput,
  endDate?: DbDateInput,
  storedStatus?: string | null
): ClassroomLifecycleStatus {
  const stored = String(storedStatus || '')
    .toUpperCase()
    .trim()
  if (stored === 'STOP' || stored === 'CANCELLED') return stored

  const todayKey = vnYmdKey(new Date())
  const endKey = vnYmdKey(endDate)
  const startKey = vnYmdKey(startDate)

  if (todayKey && endKey && todayKey > endKey) return 'FINISH'
  if (todayKey && startKey && todayKey >= startKey) return 'LEARNING'
  return 'UPCOMING'
}

export function classroomLifecycleLabel(
  startDate?: DbDateInput,
  endDate?: DbDateInput,
  storedStatus?: string | null
): string {
  const code = resolveClassroomLifecycleStatus(startDate, endDate, storedStatus)
  const map: Record<ClassroomLifecycleStatus, string> = {
    LEARNING: 'Đang học',
    FINISH: 'Hoàn thành',
    UPCOMING: 'Sắp khai giảng',
    STOP: 'Tạm dừng',
    CANCELLED: 'Đã hủy',
  }
  return map[code]
}
